import {
  type TranslationKeys,
  useTranslation,
} from "@/hooks/useTranslation/useTranslation"
import { AlertTriangle, ChevronDown, Info, ShieldAlert } from "lucide-react"
import { useState } from "react"
import type { DataQualityWarning } from "../../api/types/imports"

interface DataQualityWarningsProps {
  warnings: DataQualityWarning[]
  /**
   * Overrides the blocking count used in the collapsed toggle summary
   * (`MC_MD_DQ_ISSUE(S)_FOUND`). Callers that cap the rendered `warnings`
   * list below the true error count (TUR-1522: only the first 20 row
   * errors are rendered) pass the real total here so the toggle and the
   * separate "N more" line agree. Defaults to `blocking.length`.
   */
  totalCount?: number
  /** Start open instead of collapsed. A rolled-back import has nothing else to
   *  show, so hiding its reasons behind a toggle is the wrong default there. */
  defaultExpanded?: boolean
}

type Translate = (
  key: TranslationKeys,
  dynamicValues?: Record<string, string | number>,
) => string

/**
 * Codes that resolve through the standard "{location}: <sentence>" shape (the
 * per-row commit errors), plus the codes that carry no
 * row location at all (bad input / nothing-to-commit / commit-failed cases —
 * their translation values simply don't reference `{location}`, so it's
 * computed but unused for them). Anything not in this table falls through to
 * `w.message` — see the note on `warningText` below.
 */
const ROW_ERROR_KEYS: Record<string, TranslationKeys> = {
  // No longer emitted (a column without a supported framework is now skipped
  // with an *_FRAMEWORK_SKIPPED warning); kept so failed imports persisted
  // before that change still render translated.
  UNSUPPORTED_FRAMEWORK: "MC_MD_IMPORT_ERR_UNSUPPORTED_FRAMEWORK",
  BOM_PARENT_NOT_FOUND: "MC_MD_IMPORT_ERR_BOM_PARENT_NOT_FOUND",
  BOM_LINK_INCOMPLETE: "MC_MD_IMPORT_ERR_BOM_LINK_INCOMPLETE",
  INVALID_VALUE: "MC_MD_IMPORT_ERR_INVALID_VALUE",
  PRODUCT_NUMBER_TAKEN: "MC_MD_IMPORT_ERR_PRODUCT_NUMBER_TAKEN",
  ALL_COLUMNS_SKIPPED: "MC_MD_IMPORT_ERR_ALL_COLUMNS_SKIPPED",
  DB_ERROR: "MC_MD_IMPORT_ERR_DB_ERROR",
  // A duplicate key: names the record instead of a bare "try again".
  DB_DUPLICATE: "MC_MD_IMPORT_ERR_DB_DUPLICATE",
  NO_COLUMN_MAPPINGS: "MC_MD_IMPORT_ERR_NO_COLUMN_MAPPINGS",
  NO_DATA_ROWS: "MC_MD_IMPORT_ERR_NO_DATA_ROWS",
  // Rows were read but none matched or created a record (e.g. substances with no
  // article column): a failure, not "nothing new in this file".
  NOTHING_IMPORTED: "MC_MD_IMPORT_ERR_NOTHING_IMPORTED",
  NO_INPUT: "MC_MD_IMPORT_ERR_NO_INPUT",
  NOT_MAPPING_CONFIRMED: "MC_MD_IMPORT_ERR_NOT_MAPPING_CONFIRMED",
  COMMIT_FAILED: "MC_MD_IMPORT_ERR_COMMIT_FAILED",
  // A row whose product columns carry no usable product number. Sheet, row and
  // the offending column, so the user can go straight to the cell.
  PRODUCT_NUMBER_MISSING: "MC_MD_IMPORT_ERR_PRODUCT_NUMBER_MISSING",
}

/** `"Sheet X, row N, column Y"` (or without the column suffix), or `""` when
 * the error carries no location (the whole-import codes above).
 *
 * ROW ERRORS ONLY. A mapping-level warning has no row to point at and carries its
 * location in `context` instead, like every other analysis warning; it is rendered
 * by its own branch in `warningText` rather than through this. */
function locationText(w: DataQualityWarning, translate: Translate): string {
  if (w.sheet == null || w.row == null) return ""
  const column = w.column
    ? translate("MC_MD_IMPORT_ERR_COLUMN_SUFFIX", { column: w.column })
    : ""
  return translate("MC_MD_IMPORT_ERR_LOCATION", {
    sheet: w.sheet,
    row: w.row,
    column,
  })
}

/**
 * Text for one warning/row error. The `code` set backend/turnus_ai/pipelines/
 * mc_imports.py, mc_commit/runner.py and mc_imports_service.py emit is
 * finite. Three shapes are table-driven and translated:
 * - `duplicates` carries purely-numeric params (duplicateGroups/duplicateRows)
 *   a frontend template can safely reconstruct.
 * - `SUPPLIER_MAPPING_OVERWRITTEN` reads its old/new article numbers from
 *   `context` (a `CommitWarning`, not a row error — see `DataQualityWarning`).
 * - `UNSUPPORTED_FRAMEWORK_SKIPPED` / `UNRECOGNISED_FRAMEWORK_SKIPPED` (also
 *   `CommitWarning`s) read the skipped column (and framework) from `context`.
 * - Everything in `ROW_ERROR_KEYS` (the TUR-1522 row errors) is translated via
 *   `locationText` + `params`.
 * - `WEAK_PK` and `LOW_CONFIDENCE` (mapping-analysis warnings, TUR-1549) read
 *   their column/table/field/sheet/confidence from `context` and are
 *   translated the same way.
 * The remaining codes ('validation', 'NO_COMMITTABLE_ROWS' (persisted before
 * it became NOTHING_IMPORTED),
 * 'CUSTOMER_MAPPING_NUMBER_TAKEN') carry no structured context, so they
 * render the backend `message` as-is; only the heading/summary above (and
 * the code paths here) are translated UI copy.
 */
/** `"{column}"` alone, or `"{column} ({sheet})"` when the warning names a sheet. */
function columnLabel(
  ctx: Record<string, unknown>,
  translate: Translate,
): string {
  const column = String(ctx.column ?? "")
  const sheet = ctx.sheet ? String(ctx.sheet) : ""
  return sheet
    ? translate("MC_MD_DQ_COLUMN_IN_SHEET", { column, sheet })
    : column
}

function warningText(w: DataQualityWarning, translate: Translate): string {
  if (w.code === "WEAK_PK" && w.context) {
    return translate("MC_MD_DQ_WEAK_PK", {
      column: columnLabel(w.context, translate),
      table: String(w.context.table ?? ""),
      field: String(w.context.field ?? ""),
    })
  }
  if (w.code === "LOW_CONFIDENCE" && w.context) {
    return translate("MC_MD_DQ_LOW_CONFIDENCE", {
      column: columnLabel(w.context, translate),
      confidence: Number(w.context.confidence ?? 0),
    })
  }
  if (w.code === "UNSUPPORTED_TARGET" && w.context) {
    // An analysis warning, so its location lives in `context` like WEAK_PK's and
    // LOW_CONFIDENCE's, and `columnLabel` is what names the sheet. There is no
    // row: the mapping is wrong, not one cell in it.
    return translate("MC_MD_DQ_UNSUPPORTED_TARGET", {
      column: columnLabel(w.context, translate),
      table: String(w.context.table ?? ""),
      field: String(w.context.field ?? ""),
    })
  }
  if (w.code === "GROUP_COLUMN_AMBIGUOUS" && w.context) {
    const columns = Array.isArray(w.context.columns)
      ? (w.context.columns as string[]).join(", ")
      : ""
    // Since the check became per sheet (T1), two sheets with one group column
    // each are fine and only ONE sheet is ever at fault, so the message names it.
    // Without the sheet the user has to guess which of them to fix.
    const sheet = w.context.sheet ? String(w.context.sheet) : ""
    return sheet
      ? translate("MC_MD_DQ_GROUP_COLUMN_AMBIGUOUS_ON_SHEET", {
          field: translate("MC_MD_FIELD_PRODUCT_GROUP"),
          columns,
          sheet,
        })
      : translate("MC_MD_DQ_GROUP_COLUMN_AMBIGUOUS", {
          field: translate("MC_MD_FIELD_PRODUCT_GROUP"),
          columns,
        })
  }
  if (
    w.code === "duplicates" &&
    w.duplicateGroups != null &&
    w.duplicateRows != null
  ) {
    return translate("MC_MD_DQ_DUPLICATES_DETAIL", {
      groups: w.duplicateGroups,
      rows: w.duplicateRows,
    })
  }
  if (w.code === "ROUTING_LINES_SKIPPED" && w.context) {
    // In context, not params: the commit warning schema carries code, message
    // and context only, so a params count would be lost after the commit.
    return translate("MC_MD_IMPORT_WARN_ROUTING_LINES_SKIPPED", {
      count: String(w.context.count ?? ""),
    })
  }
  if (w.code === "ARTICLE_FROM_FILE_NAME" && w.context) {
    return translate("MC_MD_IMPORT_WARN_ARTICLE_FROM_FILE_NAME", {
      articleNumber: String(w.context.articleNumber ?? ""),
    })
  }
  if (w.code === "SCIP_VALUES_SKIPPED" && w.context) {
    const columns = Array.isArray(w.context.columns)
      ? (w.context.columns as string[]).join(", ")
      : ""
    return translate("MC_MD_IMPORT_WARN_SCIP_VALUES_SKIPPED", {
      count: String(w.context.count ?? ""),
      columns,
    })
  }
  if (w.code === "TARGET_STORED_AS_ATTRIBUTE" && w.context) {
    return translate("MC_MD_IMPORT_WARN_TARGET_STORED_AS_ATTRIBUTE", {
      column: columnLabel(w.context, translate),
    })
  }
  if (w.code === "SUPPLIER_MAPPING_OVERWRITTEN" && w.context) {
    // The axios response interceptor camelCases every object key recursively
    // (`convertKeysToCamelCase`, unconditional and deep — see
    // services/api/apiClient.ts / adapters/caseConversion), so the backend's
    // `old_number`/`new_number` arrive here as `oldNumber`/`newNumber`.
    return translate("MC_MD_DQ_SUPPLIER_MAPPING_OVERWRITTEN", {
      old: String(w.context.oldNumber ?? ""),
      new: String(w.context.newNumber ?? ""),
    })
  }
  if (w.code === "UNSUPPORTED_FRAMEWORK_SKIPPED" && w.context) {
    // A commit warning (the import went through, one compliance column did
    // not): `column` is the header as it appears in the customer's file,
    // `framework` the regulation read from it.
    return translate("MC_MD_DQ_UNSUPPORTED_FRAMEWORK_SKIPPED", {
      column: String(w.context.column ?? ""),
      framework: String(w.context.framework ?? ""),
    })
  }
  if (w.code === "UNRECOGNISED_FRAMEWORK_SKIPPED" && w.context) {
    // Same, for a header in which no framework could be recognised at all.
    return translate("MC_MD_DQ_UNRECOGNISED_FRAMEWORK_SKIPPED", {
      column: String(w.context.column ?? ""),
    })
  }
  const key = ROW_ERROR_KEYS[w.code]
  if (key) {
    return translate(key, {
      location: locationText(w, translate),
      ...(w.params ?? {}),
    })
  }
  return w.message
}

export function DataQualityWarnings({
  warnings,
  totalCount,
  defaultExpanded = false,
}: DataQualityWarningsProps) {
  const { translate } = useTranslation()
  const [expanded, setExpanded] = useState(defaultExpanded)

  if (!warnings.length) return null

  const blocking = warnings.filter((w) => w.severity === "blocking")
  const info = warnings.filter((w) => w.severity === "info")
  const hasBlocking = blocking.length > 0
  const blockingCount = totalCount ?? blocking.length

  const borderColor = hasBlocking ? "border-red1/20" : "border-yellow1/18"
  const bgColor = hasBlocking ? "bg-red1/4" : "bg-yellow1/4"
  const iconColor = hasBlocking ? "text-red1" : "text-yellow1"
  const Icon = hasBlocking ? ShieldAlert : Info

  const summary = hasBlocking
    ? translate(
        blockingCount === 1 ? "MC_MD_DQ_ISSUE_FOUND" : "MC_MD_DQ_ISSUES_FOUND",
        { n: blockingCount },
      )
    : translate(info.length === 1 ? "MC_MD_DQ_NOTE" : "MC_MD_DQ_NOTES", {
        n: info.length,
      })

  return (
    <div className={`rounded-lg border ${borderColor} ${bgColor}`}>
      <button
        type="button"
        aria-expanded={expanded}
        className="flex w-full items-center gap-2.5 px-4 py-2.5"
        onClick={() => setExpanded((v) => !v)}
      >
        <Icon size={15} className={`shrink-0 ${iconColor}`} />
        <span className="flex-1 text-left text-[12.5px] font-medium text-white">
          {summary}
        </span>
        <ChevronDown
          size={14}
          className={`shrink-0 text-grey5 transition-transform ${expanded ? "rotate-180" : ""}`}
        />
      </button>

      {expanded && (
        <div className="space-y-1 border-t border-white/6 px-4 pt-2 pb-3">
          {blocking.map((w, i) => (
            <div key={`b-${i}`} className="flex items-start gap-2">
              <AlertTriangle
                size={12}
                className="mt-[3px] shrink-0 text-red1"
              />
              <span className="text-[12px] leading-[1.55] text-grey5">
                {warningText(w, translate)}
              </span>
            </div>
          ))}
          {info.map((w, i) => (
            <div key={`i-${i}`} className="flex items-start gap-2">
              <Info size={12} className="mt-[3px] shrink-0 text-yellow1" />
              <span className="text-[12px] leading-[1.55] text-grey5">
                {warningText(w, translate)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
